import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateTime } from '@/utils/datetime';
import ELK from 'elkjs';
import ImgAdding from '@/assets/images/operations/Adding.png';
import ImgCharging from '@/assets/images/operations/Charging.png';
import ImgCleaning from '@/assets/images/operations/Cleaning.png';
import ImgCooling from '@/assets/images/operations/Cooling.png';
import ImgDrying from '@/assets/images/operations/Drying.png';
import ImgFiltering from '@/assets/images/operations/Filtering.png';
import ImgHeating from '@/assets/images/operations/Heating.png';
import ImgMixing from '@/assets/images/operations/Mixing.png';
import ImgMonitoring from '@/assets/images/operations/Monitoring.png';
import ImgSampling from '@/assets/images/operations/Sampling.png';
import ImgStir from '@/assets/images/operations/Stir.png';
import ImgTableting from '@/assets/images/operations/Tableting.png';
import ImgUnloading from '@/assets/images/operations/Unloading.png';
import ImgCoating from '@/assets/images/operations/Coating.png';
import ImgBlending from '@/assets/images/operations/Blending.png';
import ImgMaintainState from '@/assets/images/operations/Maintain state.png';
import ImgMixingBlending from '@/assets/images/operations/Mixing and Blending.png';
import ImgWashing from '@/assets/images/operations/Washing.png';
import ImgExpelling from '@/assets/images/operations/Expelling.png';
import ImgExtrusion from '@/assets/images/operations/Extrusion.png';
import ImgLoading from '@/assets/images/operations/Loading.png';
import ImgSamplingReq from '@/assets/images/operations/Sampling Request.png';
import ImgPreparing from '@/assets/images/operations/Preparing.png';
import ImgLabeling from '@/assets/images/operations/Labeling.png';
import ImgDefault from '@/assets/images/operations/Default.png';
import { Badge, Tooltip, Drawer, Typography, Descriptions, Button } from 'antd';
import { Maximize, Minimize2, Minimize, X, Circle, ZoomIn, ZoomOut } from 'lucide-react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { setEditOpDag } from '@/store/reducers/editOperationDag';
import { ViewOperationDetailsCollapse } from '@/components/ViewOperationDetailsCol';
import { FinalizedOperationDetailsCollapse } from '@/components/ViewOperationDetailsCol';
import BatchProgressView from './BatchProgressView';
import {
  get_operation_operation_type,
  get_batch_details_by_process_id,
  get_all_process_data_for_operation,
} from '@/utils/api';

import {
  EXECUTION_STATUS_LEGEND,
  EXECUTION_STATUS_STYLES,
  resolveOperationExecutionStatus,
} from '@/utils/operationExecutionStatus';

// Operation icon map
const OP_ICONS = {
  Adding: ImgAdding, Charging: ImgCharging, Cleaning: ImgCleaning, Cooling: ImgCooling,
  Drying: ImgDrying, Filtering: ImgFiltering, Heating: ImgHeating, Mixing: ImgMixing,
  Monitoring: ImgMonitoring, Sampling: ImgSampling, Stir: ImgStir, Tableting: ImgTableting,
  Unloading: ImgUnloading, Coating: ImgCoating, Blending: ImgBlending,
  'Maintain state': ImgMaintainState, 'Mixing and Blending': ImgMixingBlending,
  Washing: ImgWashing, Expelling: ImgExpelling, Extrusion: ImgExtrusion, Loading: ImgLoading,
  'Sampling Request': ImgSamplingReq, Preparing: ImgPreparing, Labeling: ImgLabeling,
  Default: ImgDefault,
};

// ─── SVG ArchiMate Node ──────────────────────────────────────────────────────
function ArchiMateNode({ node, isHighlighted, onClick }) {
  const [hovered, setHovered] = useState(false);
  const resolved = resolveOperationExecutionStatus(node.data?.status ?? node.data);
  const cfg = resolved.style;
  const iconSrc = OP_ICONS[node.data?.operation] || OP_ICONS.Default;
  const w = node.w || 150;
  const h = node.h || 50;
  const isRunning = resolved.key === 'Running';
  const isUnderReview = resolved.key === 'CompletedUnderReview';
  const isHold = resolved.key === 'Paused' || resolved.key === 'Hold';

  return (
    <g
      transform={`translate(${node.x},${node.y})`}
      onClick={() => onClick(node)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ cursor: 'pointer' }}
    >
      {/* Animated pulse glow for running operations */}
      {isRunning && (
        <rect x={-4} y={-4} width={w + 8} height={h + 8} rx={6} fill="none" stroke={cfg.color} strokeWidth={2} opacity={0.3}>
          <animate attributeName="opacity" values="0.3;0.08;0.3" dur="2s" repeatCount="indefinite" />
          <animate attributeName="x" values="-4;-6;-4" dur="2s" repeatCount="indefinite" />
          <animate attributeName="y" values="-4;-6;-4" dur="2s" repeatCount="indefinite" />
          <animate attributeName="width" values={`${w + 8};${w + 12};${w + 8}`} dur="2s" repeatCount="indefinite" />
          <animate attributeName="height" values={`${h + 8};${h + 12};${h + 8}`} dur="2s" repeatCount="indefinite" />
        </rect>
      )}
      {/* Under review: supervisor must approve (critical — blocks downstream) */}
      {isUnderReview && (
        <g>
          <rect
            x={w / 2 - 44} y={-34} width={88} height={18} rx={9}
            fill={cfg.color}
          >
            <animate attributeName="y" values="-34;-38;-34" dur="1.4s" repeatCount="indefinite" />
          </rect>
          <text
            x={w / 2} y={-22} textAnchor="middle" dominantBaseline="middle"
            fontSize={9} fontWeight={700} fill="#fff" style={{ pointerEvents: 'none' }}
          >
            <animate attributeName="y" values="-22;-26;-22" dur="1.4s" repeatCount="indefinite" />
            REVIEW
          </text>
          <polygon points={`${w / 2 - 5},-16 ${w / 2 + 5},-16 ${w / 2},-9`} fill={cfg.color}>
            <animate attributeName="points"
              values={`${w / 2 - 5},-16 ${w / 2 + 5},-16 ${w / 2},-9;${w / 2 - 5},-20 ${w / 2 + 5},-20 ${w / 2},-13;${w / 2 - 5},-16 ${w / 2 + 5},-16 ${w / 2},-9`}
              dur="1.4s" repeatCount="indefinite" />
          </polygon>
        </g>
      )}
      {/* Running pointer: "RUNNING" pill + downward triangle hovering above the node */}
      {isRunning && (
        <g>
          <rect
            x={w / 2 - 38} y={-34} width={76} height={18} rx={9}
            fill={cfg.color}
          >
            <animate attributeName="y" values="-34;-38;-34" dur="1.2s" repeatCount="indefinite" />
          </rect>
          <text
            x={w / 2} y={-22} textAnchor="middle" dominantBaseline="middle"
            fontSize={10} fontWeight={700} fill="#fff" style={{ pointerEvents: 'none' }}
          >
            <animate attributeName="y" values="-22;-26;-22" dur="1.2s" repeatCount="indefinite" />
            ▶ RUNNING
          </text>
          <polygon points={`${w / 2 - 5},-16 ${w / 2 + 5},-16 ${w / 2},-9`} fill={cfg.color}>
            <animate attributeName="points"
              values={`${w / 2 - 5},-16 ${w / 2 + 5},-16 ${w / 2},-9;${w / 2 - 5},-20 ${w / 2 + 5},-20 ${w / 2},-13;${w / 2 - 5},-16 ${w / 2 + 5},-16 ${w / 2},-9`}
              dur="1.2s" repeatCount="indefinite" />
          </polygon>
        </g>
      )}
      {/* Hover glow */}
      {hovered && !isRunning && !isUnderReview && (
        <rect x={-3} y={-3} width={w + 6} height={h + 6} rx={5} fill="none" stroke={cfg.color} strokeWidth={1.5} opacity={0.4} />
      )}
      {/* Paused: static emphasis ring so paused operations stand out */}
      {isHold && (
        <rect x={-3} y={-3} width={w + 6} height={h + 6} rx={5} fill="none" stroke={cfg.color} strokeWidth={2.5} opacity={0.55} />
      )}
      {/* Node body */}
      <rect
        x={0} y={0} width={w} height={h} rx={3}
        fill={hovered ? cfg.border + '33' : (cfg.fillBg || cfg.bg)}
        stroke={isHighlighted ? '#1e90ff' : hovered ? cfg.color : cfg.border}
        strokeWidth={isHighlighted ? 2.5 : isHold ? 2.5 : hovered ? 1.5 : 1}
      />
      {/* Operation icon */}
      <image href={iconSrc} x={8} y={(h - 26) / 2} width={26} height={26} style={{ filter: cfg.filter }} />
      {/* Operation name */}
      <text
        x={40 + (w - 40) / 2} y={h / 2 + 1}
        textAnchor="middle" dominantBaseline="middle"
        fontSize={13} fontFamily="sans-serif" fontWeight={hovered || isHold ? 700 : 500} fill={cfg.text}
      >
        {(node.data?.operation || '').length > 18
          ? (node.data?.operation || '').slice(0, 16) + '…'
          : node.data?.operation || ''}
      </text>
      {/* Operation number badge with status color — always visible at top-left */}
      <g>
        <circle cx={0} cy={0} r={10} fill={cfg.color} stroke="#fff" strokeWidth={1.5} />
        <text x={0} y={0.5} textAnchor="middle" dominantBaseline="middle" fontSize={9} fontWeight={700} fill="#fff" fontFamily="sans-serif">
          {node.data?.operation_number ?? node.data?.operationId ?? ''}
        </text>
      </g>
    </g>
  );
}

// ─── Process color palette ──────────────────────────────────────────────────
const PROCESS_PALETTE = [
  { stroke: '#3b82f6', bg: 'rgba(59,130,246,0.08)' },   // blue
  { stroke: '#10b981', bg: 'rgba(16,185,129,0.08)' },   // emerald
  { stroke: '#f59e0b', bg: 'rgba(245,158,11,0.08)' },   // amber
  { stroke: '#8b5cf6', bg: 'rgba(139,92,246,0.08)' },   // violet
  { stroke: '#ec4899', bg: 'rgba(236,72,153,0.08)' },   // pink
  { stroke: '#06b6d4', bg: 'rgba(6,182,212,0.08)' },    // cyan
  { stroke: '#84cc16', bg: 'rgba(132,204,22,0.08)' },   // lime
  { stroke: '#ef4444', bg: 'rgba(239,68,68,0.08)' },    // red
  { stroke: '#0ea5e9', bg: 'rgba(14,165,233,0.08)' },   // sky
  { stroke: '#a855f7', bg: 'rgba(168,85,247,0.08)' },   // purple
];
const colorForProcess = (idx) => PROCESS_PALETTE[idx % PROCESS_PALETTE.length];

// ─── SVG Group box (renders behind everything) ──────────────────────────────
function ArchiMateGroupBox({ group }) {
  const c = group.color || { stroke: '#c0c0c0', bg: 'rgba(0,0,0,0.015)' };
  return (
    <g transform={`translate(${group.x},${group.y})`}>
      <rect x={0} y={0} width={group.w} height={group.h} rx={6} ry={6}
        fill={c.bg} stroke={c.stroke} strokeWidth={1.5} strokeDasharray="5,3"
      />
    </g>
  );
}
// ─── SVG Group label (renders on top of everything) ─────────────────────────
function ArchiMateGroupLabel({ group }) {
  const c = group.color || { stroke: '#204448' };
  return (
    <g transform={`translate(${group.x},${group.y})`}>
      <text x={group.w / 2} y={-4} textAnchor="middle" fontSize={13} fontWeight={700} fill={c.stroke} fontFamily="sans-serif">
        {group.label || ''}
      </text>
    </g>
  );
}

// ─── Edge renderer ───────────────────────────────────────────────────────────
function midPointOfPath(pathD) {
  if (!pathD) return null;
  const pts = [];
  pathD.split(/(?=[MLC])/).forEach((cmd) => {
    const tokens = cmd.trim().slice(1).trim().split(/[ ,]+/).map(Number);
    if (cmd[0] === 'C' && tokens.length >= 6) pts.push({ x: tokens[4], y: tokens[5] });
    else if ((cmd[0] === 'M' || cmd[0] === 'L') && tokens.length >= 2) pts.push({ x: tokens[0], y: tokens[1] });
  });
  // Fallback: pull every number pair out of the path string. Handles any command
  // shape (e.g. curves whose anchor points the structured parse above missed).
  if (pts.length < 2) {
    const nums = (pathD.match(/-?\d+(\.\d+)?/g) || []).map(Number);
    const all = [];
    for (let i = 0; i + 1 < nums.length; i += 2) all.push({ x: nums[i], y: nums[i + 1] });
    if (all.length >= 2) {
      const m = Math.floor(all.length / 2);
      const a = all[m - 1], b = all[m];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }
    return null;
  }
  const m = Math.floor(pts.length / 2);
  const a = pts[m - 1], b = pts[m];
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

const DEP_STYLE = {
  pass: { color: '#28A745', label: 'Pass', bg: '#e6f7ed' },
  fail: { color: '#DC3545', label: 'Fail', bg: '#fdecec' },
  machine: { color: '#16a34a', label: '', bg: '#dcfce7' },
  normal: { color: '#94a3b8', label: '', bg: null },
};

function ArchiMateEdge({ pathD, edgeId, depType, sourceColor, machineGroupNames }) {
  if (!pathD) return null;
  const mid = `arr-${edgeId}`;
  const baseStyle = DEP_STYLE[depType] || DEP_STYLE.normal;
  // For normal cross-process edges, tint with source process color; pass/fail/machine keep their semantic colors
  const strokeColor = depType === 'normal' && sourceColor ? sourceColor.stroke : baseStyle.color;
  // Pass/Fail show their static label here. Machine-group labels are drawn in a
  // dedicated top layer (Layer 5) so long names sit above nodes — see render.
  const lbl = baseStyle.label;
  const labelPt = lbl ? midPointOfPath(pathD) : null;
  return (
    <g style={{ pointerEvents: 'none' }}>
      <defs>
        <marker id={mid} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 1 L 8 5 L 0 9 z" fill={strokeColor} />
        </marker>
      </defs>
      <path d={pathD} fill="none" stroke="#e2e8f0" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
      <path d={pathD} fill="none" stroke={strokeColor} strokeWidth={1.6} strokeDasharray="5,4" strokeLinejoin="round" strokeLinecap="round" markerEnd={`url(#${mid})`}>
        <animate attributeName="stroke-dashoffset" from="18" to="0" dur="1s" repeatCount="indefinite" />
      </path>
      {labelPt && (
        <g transform={`translate(${labelPt.x},${labelPt.y})`}>
          <rect x={-16} y={-8} width={32} height={14} rx={3} fill={baseStyle.bg} stroke={baseStyle.color} strokeWidth={0.8} />
          <text x={0} y={0} textAnchor="middle" dominantBaseline="middle" fontSize={9} fontWeight={700} fill={baseStyle.color}>{lbl}</text>
        </g>
      )}
    </g>
  );
}

// ─── Compute ALL edge paths — strict orthogonal, outside all nodes ───────────
function computeEdgePaths(edges, nodesMap, groups) {
  const GAP = 18;
  const allNodes = Object.values(nodesMap);
  const paths = {};

  let gMinY = Infinity, gMaxY = 0;
  (groups || []).forEach((g) => { gMinY = Math.min(gMinY, g.y); gMaxY = Math.max(gMaxY, g.y + g.h); });
  allNodes.forEach((n) => { gMinY = Math.min(gMinY, n.y); gMaxY = Math.max(gMaxY, n.y + (n.h || 36)); });
  if (gMinY === Infinity) gMinY = 0;

  // Classify edges
  const edgeList = edges.map((e) => {
    const src = nodesMap[e.source], tgt = nodesMap[e.target];
    if (!src || !tgt) return null;
    return { ...e, src, tgt, same: String(src.data?.process) === String(tgt.data?.process) };
  }).filter(Boolean);

  // Pick best side for source→target based on relative position
  // Source prefers R (default for left-to-right flow); target prefers L.
  // If target is above/below and not significantly to the right, use T/B.
  const pickSides = (src, tgt) => {
    const sw = src.w || 120, sh = src.h || 36;
    const tw = tgt.w || 120, th = tgt.h || 36;
    // Face center points
    const sFaces = { R: [src.x + sw, src.y + sh / 2], L: [src.x, src.y + sh / 2], T: [src.x + sw / 2, src.y], B: [src.x + sw / 2, src.y + sh] };
    const tFaces = { R: [tgt.x + tw, tgt.y + th / 2], L: [tgt.x, tgt.y + th / 2], T: [tgt.x + tw / 2, tgt.y], B: [tgt.x + tw / 2, tgt.y + th] };
    let best = null;
    for (const s of ['R', 'L', 'T', 'B']) {
      for (const t of ['R', 'L', 'T', 'B']) {
        // Reject sides facing away from each other
        const [sx, sy] = sFaces[s], [tx, ty] = tFaces[t];
        // Source side direction must roughly point toward target (and vice versa)
        const sDir = s === 'R' ? [1, 0] : s === 'L' ? [-1, 0] : s === 'B' ? [0, 1] : [0, -1];
        const tDir = t === 'R' ? [1, 0] : t === 'L' ? [-1, 0] : t === 'B' ? [0, 1] : [0, -1];
        const vx = tx - sx, vy = ty - sy;
        if (sDir[0] * vx + sDir[1] * vy < 0) continue;
        if (tDir[0] * -vx + tDir[1] * -vy < 0) continue;
        const d = Math.abs(vx) + Math.abs(vy);
        if (!best || d < best.d) best = { d, sSide: s, tSide: t };
      }
    }
    return best || { sSide: 'R', tSide: 'L' };
  };

  // Pre-compute sides and count per port. Cross-process edges always run between
  // process boxes laid out left-to-right, so force a clean Right→Left horizontal
  // (source exits the right face, target enters the left face).
  edgeList.forEach((ei) => {
    if (!ei.same) {
      ei.sSide = ei.tgt.x >= ei.src.x ? 'R' : 'L';
      ei.tSide = ei.tgt.x >= ei.src.x ? 'L' : 'R';
    } else {
      const { sSide, tSide } = pickSides(ei.src, ei.tgt);
      ei.sSide = sSide; ei.tSide = tSide;
    }
  });
  const portTotal = {};
  const portUsed = {};
  edgeList.forEach((ei) => {
    portTotal[ei.source + '_' + ei.sSide] = (portTotal[ei.source + '_' + ei.sSide] || 0) + 1;
    portTotal[ei.target + '_' + ei.tSide] = (portTotal[ei.target + '_' + ei.tSide] || 0) + 1;
  });

  const getPort = (node, side, key) => {
    const nw = node.w || 120, nh = node.h || 36;
    portUsed[key] = portUsed[key] || 0;
    const idx = portUsed[key]++;
    const total = portTotal[key] || 1;
    const pad = 4;
    if (side === 'R' || side === 'L') {
      const range = nh - pad * 2;
      const step = range / (total + 1);
      const off = pad + step * (idx + 1);
      return { x: side === 'R' ? node.x + nw : node.x, y: node.y + off, side };
    }
    // T or B
    const range = nw - pad * 2;
    const step = range / (total + 1);
    const off = pad + step * (idx + 1);
    return { x: node.x + off, y: side === 'T' ? node.y : node.y + nh, side };
  };

  // Horizontal lanes (between rows + above/below)
  const lanes = [];
  {
    const rows = [];
    allNodes.forEach((n) => {
      const top = n.y, bot = n.y + (n.h || 36);
      let r = rows.find((rr) => Math.abs(rr.top - top) < 8);
      if (!r) rows.push({ top, bot }); else r.bot = Math.max(r.bot, bot);
    });
    rows.sort((a, b) => a.top - b.top);
    lanes.push({ y: rows[0] ? rows[0].top - 45 : gMinY - 45, occupied: [] });
    for (let i = 0; i < rows.length - 1; i++) {
      const gap = rows[i + 1].top - rows[i].bot;
      if (gap >= 20) lanes.push({ y: (rows[i].bot + rows[i + 1].top) / 2, occupied: [] });
    }
    lanes.push({ y: rows.length ? rows[rows.length - 1].bot + 45 : gMaxY + 45, occupied: [] });
  }
  // Node keep-clear boxes: the node footprint padded by NODE_MARGIN. Edges must
  // stay outside this box entirely (not just outside the raw node), so they don't
  // graze or overlap the node border.
  const NODE_MARGIN = 12;
  const nodeBoxes = allNodes.map((n) => ({
    id: n.id,
    x1: n.x - NODE_MARGIN,
    y1: n.y - NODE_MARGIN,
    x2: n.x + (n.w || 120) + NODE_MARGIN,
    y2: n.y + (n.h || 36) + NODE_MARGIN,
  }));
  // A horizontal segment at y spanning [xa,xb] touches/enters a keep-clear box?
  // Inclusive bounds (>=,<=) so a run lying exactly on the padded border counts.
  // `exclude` = set of node ids to ignore (an edge may touch its OWN endpoints).
  const hSegHitsNode = (y, xa, xb, exclude) => {
    const lo = Math.min(xa, xb), hi = Math.max(xa, xb);
    return nodeBoxes.some((b) =>
      !(exclude && exclude.has(b.id)) &&
      y >= b.y1 && y <= b.y2 && hi >= b.x1 && lo <= b.x2);
  };
  // A vertical segment at x spanning [ya,yb] touches/enters a keep-clear box?
  const vSegHitsNode = (x, ya, yb, exclude) => {
    const lo = Math.min(ya, yb), hi = Math.max(ya, yb);
    return nodeBoxes.some((b) =>
      !(exclude && exclude.has(b.id)) &&
      x >= b.x1 && x <= b.x2 && hi >= b.y1 && lo <= b.y2);
  };

  // Two horizontal runs in the same lane overlap unless their x-spans are clear
  // of each other by this margin. Bumped from 20 so near-parallel runs separate.
  const LANE_CLEAR = 24;
  const laneFree = (l, x1, x2) =>
    !l.occupied.some(([a, b]) => !(x2 < a - LANE_CLEAR || x1 > b + LANE_CLEAR)) &&
    !hSegHitsNode(l.y, x1, x2);
  const pickLane = (x1, x2, preferY) => {
    const ranked = lanes.map((l) => ({ l, d: Math.abs(l.y - preferY) })).sort((a, b) => a.d - b.d);
    for (const { l } of ranked) {
      if (laneFree(l, x1, x2)) { l.occupied.push([x1, x2]); return l.y; }
    }
    // No existing lane fits — create new overflow lanes above or below, stepping
    // until one is clear of nodes AND of every other run we put there. Each new
    // lane is a real lane with its own occupancy so later edges reuse/avoid it.
    const above = preferY < (gMinY + gMaxY) / 2;
    const base = above ? lanes[0].y : lanes[lanes.length - 1].y;
    const dir = above ? -1 : 1;
    for (let i = 1; i <= 60; i++) {
      const y = base + dir * i * GAP;
      let lane = lanes.find((l) => Math.abs(l.y - y) < 1);
      if (!lane) { lane = { y, occupied: [] }; lanes.push(lane); }
      if (laneFree(lane, x1, x2)) { lane.occupied.push([x1, x2]); return lane.y; }
    }
    // Last resort: a far lane, still reserved.
    const y = base + dir * 61 * GAP;
    lanes.push({ y, occupied: [[x1, x2]] });
    return y;
  };
  const stubOutCount = {}, stubInCount = {};
  const verticals = [];
  // Straight horizontal runs already placed, so parallel straight edges at the
  // same Y don't stack on top of each other.
  const straightRuns = [];
  const adjustVerticalX = (x, y1, y2, dir) => {
    for (let tries = 0; tries < 60; tries++) {
      const cur = x + dir * 16 * tries;
      // Reject if it overlaps another vertical run OR passes through a node box.
      const clash = verticals.some((v) => Math.abs(v.x - cur) < 16 && !(y2 < Math.min(v.y1, v.y2) - 8 || y1 > Math.max(v.y1, v.y2) + 8));
      if (!clash && !vSegHitsNode(cur, y1, y2)) { verticals.push({ x: cur, y1, y2 }); return cur; }
    }
    const fallback = x + dir * 300;
    verticals.push({ x: fallback, y1, y2 });
    return fallback;
  };

  edgeList.forEach((ei) => {
    // Same-process edges are routed by ELK (orthogonal, node-avoiding, short).
    // Use that route directly when available.
    if (ei.same && ei.elkPath) {
      paths[ei.id] = ei.elkPath;
      return;
    }

    const p1 = getPort(ei.src, ei.sSide, ei.source + '_' + ei.sSide);
    const p2 = getPort(ei.tgt, ei.tSide, ei.target + '_' + ei.tSide);

    // Straight-first: if the two ports share a Y and a direct line is clear of
    // every node AND of every other straight run already placed at that Y, draw
    // it straight. Otherwise fall through to the lane router, which keeps
    // parallels in separate lanes and routes the vertical jog clear of nodes.
    // 1) STRAIGHT: ports share a Y and the direct line is clear → shortest path.
    if (Math.abs(p1.y - p2.y) < 2) {
      const y = p1.y;
      const lo = Math.min(p1.x, p2.x), hi = Math.max(p1.x, p2.x);
      const overlapsStraight = straightRuns.some(
        (r) => Math.abs(r.y - y) < 10 && !(hi < r.x1 - 8 || lo > r.x2 + 8)
      );
      const selfIds = new Set([ei.src.id, ei.tgt.id]);
      if (!hSegHitsNode(y, p1.x, p2.x, selfIds) && !overlapsStraight) {
        straightRuns.push({ y, x1: lo, x2: hi });
        paths[ei.id] = `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y}`;
        return;
      }
    }
    // 2) DIRECT STEP: target is essentially in front — take the shortest single-jog
    //    orthogonal path (out from source, one vertical step, into target) as long
    //    as NO node sits in the way. Only if a node blocks a segment do we fall
    //    through to the lane router (which detours above/below the diagram). This
    //    stops edges from taking a long way around when the route is actually clear.
    //    The checks ignore the edge's own source/target nodes (the line must touch
    //    their borders to attach — that is not a collision).
    if (p2.x > p1.x) {
      const midX = (p1.x + p2.x) / 2;
      const selfIds = new Set([ei.src.id, ei.tgt.id]);
      if (
        !hSegHitsNode(p1.y, p1.x, midX, selfIds) &&
        !vSegHitsNode(midX, p1.y, p2.y, selfIds) &&
        !hSegHitsNode(p2.y, midX, p2.x, selfIds)
      ) {
        paths[ei.id] = `M ${p1.x} ${p1.y} L ${midX} ${p1.y} L ${midX} ${p2.y} L ${p2.x} ${p2.y}`;
        return;
      }
    }
    // 3) BLOCKED: a node is in the direct route → detour via a clear routing lane.
    stubOutCount[ei.source] = (stubOutCount[ei.source] || 0) + 1;
    stubInCount[ei.target] = (stubInCount[ei.target] || 0) + 1;
    const STUB1 = 22 + (stubOutCount[ei.source] - 1) * 12;
    const STUB2 = 22 + (stubInCount[ei.target] - 1) * 12;
    let x1 = p1.x + STUB1, x2 = p2.x - STUB2;
    const preferY = (p1.y + p2.y) / 2;
    const routeY = pickLane(x1, x2, preferY);
    x1 = adjustVerticalX(x1, p1.y, routeY, +1);
    x2 = adjustVerticalX(x2, routeY, p2.y, -1);
    paths[ei.id] = `M ${p1.x} ${p1.y} L ${x1} ${p1.y} L ${x1} ${routeY} L ${x2} ${routeY} L ${x2} ${p2.y} L ${p2.x} ${p2.y}`;
  });

  return paths;
}

// ─── Main Component ──────────────────────────────────────────────────────────
function ArchiMateFlowInner({ data, set_flow_data, isClickable, height }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const handle = useFullScreenHandle();
  const dispatch = useDispatch();
  const operationDagEdit = useSelector((state) => state?.editOpDag);

  const [allData, setAllData] = useState(null);
  const [dagNodes, setDagNodes] = useState([]);
  const [progressView, setProgressView] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [record, setRecord] = useState(null);

  // Layout state
  const [groups, setGroups] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [viewBox, setViewBox] = useState('0 0 800 300');
  // Content bounds from the last layout, used to (re)compute the viewBox once the
  // SVG is actually measured so nodes always render at their fixed pixel size.
  const contentBoundsRef = useRef({ minX: -30, minY: 0, w: 800, h: 300 });

  // Pan/Zoom
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });

  // ─── Fetch data ──────────────────────────────────────────────────────────
  useEffect(() => {
    console.log('[ArchiMateFlow] data prop changed:', data);
    if (!data) return;

    const batchNum = data.batch_number;
    const recipeId = data.recipe_id;
    if (!batchNum && !recipeId) return;

    const params = batchNum ? { batch_number: batchNum } : { recipe_id: recipeId };
    console.log('[ArchiMateFlow] calling API with params:', params);

    const doFetch = () => {
      get_operation_operation_type(params, (res) => {
        console.log('[ArchiMateFlow] API raw response:', res?.data);
        const apiData = res?.data?.data?.[0];
        if (apiData) {
          setAllData(apiData);
          setDagNodes(
            apiData.operation_list
              ?.filter((item) => {
                const r = resolveOperationExecutionStatus(item);
                return r.key === 'Running' || r.key === 'CompletedUnderReview';
              })
              ?.map((item) => item?.operationId) || []
          );
        }
      });
    };

    doFetch();
    const interval = setInterval(doFetch, 60000);
    return () => clearInterval(interval);
  }, [data]);

  // ─── ELK Layout ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!allData || progressView) return;
    runLayout(allData);
  }, [allData, progressView, operationDagEdit]);

  const runLayout = async (apiData) => {
    const { operation_list, process_list, operation_edges } = apiData;
    if (!operation_list?.length) return;

    const elk = new ELK();
    const PROCESS_GAP = 60;
    const PAD = 20;  // equal padding on all sides between the process box and its operation nodes
    // Fixed, larger node size so operations stay readable even with many nodes.
    const NODE_W = 170;
    const NODE_H = 52;

    // Layout each process independently then place serially left-to-right
    const sortedProcessList = [...process_list].sort((a, b) => {
      const sa = a.sequence ?? a.process_sequence ?? a.order ?? Number(a.process_id);
      const sb = b.sequence ?? b.process_sequence ?? b.order ?? Number(b.process_id);
      return sa - sb;
    });
    const processResults = [];
    for (const proc of sortedProcessList) {
      const procOps = operation_list.filter((op) => String(op.process) === String(proc.process_id));
      if (!procOps.length) {
        processResults.push({ proc, children: [], width: 120, height: 50 });
        continue;
      }

      const procOpIds = new Set(procOps.map((op) => `Op_${op.operationId}`));
      const procEdges = operation_edges.filter((e) => {
        return procOpIds.has(`Op_${e.source || e.from}`) && procOpIds.has(`Op_${e.target || e.to}`);
      });

      try {
        const result = await elk.layout({
          id: `p_${proc.process_id}`,
          layoutOptions: {
            'elk.algorithm': 'layered',
            'elk.direction': 'RIGHT',
            'elk.spacing.nodeNode': '36',
            'elk.layered.spacing.nodeNodeBetweenLayers': '70',
            'elk.layered.spacing.edgeNodeBetweenLayers': '30',
            // Keep a linear operation chain on ONE straight horizontal line:
            // network-simplex placement + node alignment avoids the staggered/
            // zig-zag rows the brandes-koepf default produces.
            'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
            'elk.layered.nodePlacement.favorStraightEdges': 'true',
            'elk.padding': '[top=10,left=10,bottom=10,right=10]',
            // Orthogonal edge routing that avoids nodes; ELK returns bend points.
            'elk.edgeRouting': 'ORTHOGONAL',
            // Keep parallel edges in separate channels instead of stacking them
            // on the same line: give edges their own spacing and never merge
            // shared segments.
            'elk.spacing.edgeEdge': '15',
            'elk.spacing.edgeNode': '15',
            'elk.layered.spacing.edgeEdgeBetweenLayers': '15',
            'elk.layered.mergeEdges': 'false',
            'elk.layered.mergeHierarchyEdges': 'false',
            // Lay independent (unconnected) operations along the flow direction
            // (horizontally) instead of stacking them in one vertical column.
            'elk.separateConnectedComponents': 'true',
            'elk.layered.components.compaction': 'true',
            'elk.layered.compaction.connectedComponents': 'true',
            'elk.layered.compaction.postCompaction.strategy': 'LEFT',
            // Stack nodes top-to-bottom in input (operation-number) order, so the
            // first operation sits at the top instead of the bottom.
            'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
            'elk.layered.crossingMinimization.forceNodeModelOrder': 'true',
          },
          children: [...procOps]
            .sort((a, b) => Number(a.operationId) - Number(b.operationId))
            .map((op) => ({ id: `Op_${op.operationId}`, width: NODE_W, height: NODE_H })),
          edges: procEdges.map((e) => ({
            // key edges by the real op pair so we can match ELK's route back later
            id: `Op_${e.source || e.from}__Op_${e.target || e.to}`,
            sources: [`Op_${e.source || e.from}`],
            targets: [`Op_${e.target || e.to}`],
          })),
        });
        processResults.push({ proc, children: result.children || [], edges: result.edges || [], width: result.width || NODE_W, height: result.height || NODE_H });
      } catch (err) {
        console.error('ELK error for process', proc.process_id, err);
      }
    }

    // Place processes horizontally
    const newGroups = [];
    const newNodes = [];
    const newEdges = [];
    // ELK-routed paths for same-process edges, keyed by `Op_src__Op_tgt`.
    const elkEdgePaths = {};
    let offsetX = 0;

    const processColorMap = {};
    processResults.forEach((pr, idx) => {
      processColorMap[String(pr.proc.process_id)] = colorForProcess(idx);
    });

    for (let pi = 0; pi < processResults.length; pi++) {
      const pr = processResults[pi];
      const color = processColorMap[String(pr.proc.process_id)];
      const oy = PAD; // label is drawn ABOVE the box (y=-4), so top padding = PAD like every other side

      // Fix ELK's vertical order within each column: assign Y slots top-to-bottom
      // by operation number so op 1 is always at the top.
      const opNumOf = (child) => Number(String(child.id).replace(/^Op_/, '')) || 0;
      const columns = {};
      for (const child of pr.children) {
        const col = Math.round(child.x || 0);
        (columns[col] = columns[col] || []).push(child);
      }
      const yByChildId = {};
      Object.values(columns).forEach((col) => {
        const slots = col.map((c) => c.y || 0).sort((a, b) => a - b);
        const ordered = [...col].sort((a, b) => opNumOf(a) - opNumOf(b));
        ordered.forEach((c, i) => { yByChildId[c.id] = slots[i]; });
      });

      // Anchor: shift so the leftmost/topmost node is flush at the padding origin.
      let minX = Infinity, minY = Infinity;
      for (const child of pr.children) {
        minX = Math.min(minX, child.x || 0);
        minY = Math.min(minY, yByChildId[child.id] ?? (child.y || 0));
      }
      if (!isFinite(minX)) minX = 0;
      if (!isFinite(minY)) minY = 0;

      // Compute gw/gh from actual node extents after anchoring so all four sides
      // have equal PAD — avoids the ELK-internal-padding imbalance.
      let maxNodeRight = NODE_W + PAD, maxNodeBottom = NODE_H + oy;
      for (const child of pr.children) {
        const nx = PAD + (child.x || 0) - minX;
        const ny = oy + (yByChildId[child.id] ?? (child.y || 0)) - minY;
        maxNodeRight = Math.max(maxNodeRight, nx + (child.width || NODE_W));
        maxNodeBottom = Math.max(maxNodeBottom, ny + (child.height || NODE_H));
      }
      const gw = maxNodeRight + PAD;
      const gh = maxNodeBottom + PAD;
      const ox = offsetX + PAD;

      newGroups.push({
        id: pr.proc.process_id,
        x: offsetX, y: 0, w: gw, h: gh,
        label: pr.proc.process || pr.proc.process_name || `Process ${pr.proc.process_id}`,
        color,
      });

      for (const child of pr.children) {
        const op = operation_list.find((o) => `Op_${o.operationId}` === child.id);
        newNodes.push({
          id: child.id,
          x: ox + (child.x || 0) - minX,
          y: oy + (yByChildId[child.id] ?? (child.y || 0)) - minY,
          w: child.width || NODE_W,
          h: child.height || NODE_H,
          data: op || {},
        });
      }

      // Convert ELK's orthogonal edge routes to SVG paths.
      for (const ee of pr.edges || []) {
        const sec = ee.sections && ee.sections[0];
        if (!sec) continue;
        const pts = [sec.startPoint, ...(sec.bendPoints || []), sec.endPoint];
        const d = pts
          .map((p, i) => `${i === 0 ? 'M' : 'L'} ${ox + p.x - minX} ${oy + p.y - minY}`)
          .join(' ');
        elkEdgePaths[ee.id] = d;
      }

      offsetX += gw + PROCESS_GAP;
    }

    operation_edges.forEach((e, i) => {
      const srcOp = operation_list.find((o) => String(o.operationId) === String(e.source || e.from));
      const tgtOp = operation_list.find((o) => String(o.operationId) === String(e.target || e.to));
      const sameProc = srcOp && tgtOp && String(srcOp.process) === String(tgtOp.process);
      const sourceColor = srcOp ? processColorMap[String(srcOp.process)] : null;
      const elkKey = `Op_${e.source || e.from}__Op_${e.target || e.to}`;
      newEdges.push({
        id: `e${i}`,
        source: `Op_${e.source || e.from}`,
        target: `Op_${e.target || e.to}`,
        depType: e.dep_type || 'normal',
        machineGroupNames: e.machine_group_names || '',
        crossProcess: !sameProc,
        sourceColor: !sameProc ? sourceColor : null,
        // ELK-computed orthogonal route for same-process edges (node-avoiding,
        // short). Cross-process edges have none and use the lane router.
        elkPath: sameProc ? elkEdgePaths[elkKey] : null,
      });
    });

    setGroups(newGroups);
    setNodes(newNodes);
    setEdges(newEdges);

    let maxX = 0, maxY = 0;
    newGroups.forEach((g) => { maxX = Math.max(maxX, g.x + g.w); maxY = Math.max(maxY, g.y + g.h); });
    newNodes.forEach((n) => { maxX = Math.max(maxX, n.x + (n.w || NODE_W)); maxY = Math.max(maxY, n.y + (n.h || NODE_H)); });
    // Extra space below for cross-process edge routing
    let crossEdgeCount = 0;
    newEdges.forEach((e) => {
      const s = newNodes.find((n) => n.id === e.source);
      const t = newNodes.find((n) => n.id === e.target);
      if (s && t && String(s.data?.process) !== String(t.data?.process)) crossEdgeCount++;
    });
    const topLanes = Math.ceil(crossEdgeCount / 2);
    const bottomLanes = Math.floor(crossEdgeCount / 2);
    // Min 44 so the "RUNNING" pointer hovering above a top-row node isn't clipped.
    const topPad = topLanes > 0 ? Math.max(44, 25 + topLanes * 12) : 44;
    const bottomPad = bottomLanes > 0 ? 25 + bottomLanes * 12 : 30;
    if (bottomLanes > 0) maxY += bottomPad;

    // Keep nodes at a fixed, readable on-screen size: make the viewBox match the
    // SVG's rendered pixel size (1 unit ≈ 1px) instead of stretching it to fit
    // the whole graph (which shrinks every node when there are many). With many
    // nodes the graph extends past the view and the user pans/zooms to explore.
    const contentW = maxX + 60;
    const contentH = maxY + topPad + 30;
    contentBoundsRef.current = { minX: -30, minY: -topPad, w: contentW, h: contentH };
    applyViewBox();
    setPan({ x: 0, y: 0 });
    setZoom(1);
  };

  // Compute the viewBox from the measured SVG size so 1 viewBox unit ≈ 1px and
  // nodes never shrink (cap the viewBox to the content extent when smaller).
  const applyViewBox = useCallback(() => {
    const { minX, minY, w, h } = contentBoundsRef.current;
    const rect = svgRef.current?.getBoundingClientRect();
    const vbW = rect?.width ? Math.min(w, rect.width) : w;
    const vbH = rect?.height ? Math.min(h, rect.height) : h;
    setViewBox(`${minX} ${minY} ${vbW} ${vbH}`);
  }, []);

  // ─── Node click ──────────────────────────────────────────────────────────
  const handleNodeClick = (node) => {
    const op = node.data;
    if (!op?.operationId) return;

    if (data?.batch_number) {
      get_batch_details_by_process_id(
        (res) => {
          if (res?.data?.status === 'success') {
            setIsDrawerOpen(true);
            const opr = res?.data?.data?.[0]?.operations?.find(
              (rec) => rec?.operation_id === parseInt(op.operationId)
            );
            setRecord(opr || null);
          }
        },
        {
          batch_number: data.batch_number,
          process_id: op.process,
          operation_id: op.operationPk || op.operationId,
        }
      );
    } else {
      get_all_process_data_for_operation(
        (res) => {
          if (res?.data?.status === 'success' && res?.data?.data?.[0]?.operations?.length > 0) {
            setRecord(res.data.data[0].operations[0]);
            setIsDrawerOpen(true);
          }
        },
        {
          recipe_id: op.recipe_id,
          process_id: op.process,
          operation_id: op.operationPk,
        }
      );
    }
  };

  // ─── Pan handlers ────────────────────────────────────────────────────────
  // Convert a screen-pixel delta into viewBox (diagram) units. The <g> transform
  // lives inside the SVG's viewBox coordinate space, so a raw clientX delta would
  // move the content at the wrong rate (the "doesn't track the cursor" bug).
  const screenToViewScale = () => {
    const el = svgRef.current;
    const parts = String(viewBox).split(/\s+/).map(Number);
    if (!el || parts.length !== 4) return 1;
    const rect = el.getBoundingClientRect();
    if (!rect.width) return 1;
    return parts[2] / rect.width; // viewBox width / rendered pixel width
  };
  const onMouseDown = (e) => {
    if (e.target.closest("g[style*='cursor: pointer']")) return;
    setIsPanning(true);
    // Store the pointer origin and the pan value at drag start, in viewBox units.
    const s = screenToViewScale();
    panStart.current = { x: e.clientX * s - pan.x, y: e.clientY * s - pan.y, s };
  };
  const onMouseMove = (e) => {
    if (!isPanning) return;
    const s = panStart.current.s || screenToViewScale();
    setPan({ x: e.clientX * s - panStart.current.x, y: e.clientY * s - panStart.current.y });
  };
  const onMouseUp = () => setIsPanning(false);
  // Attach wheel listener with passive:false so preventDefault works
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.12 : 0.12;
      setZoom((z) => Math.min(3, Math.max(0.3, z + delta)));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [nodes.length, progressView]);

  // ─── Zoom controls ──────────────────────────────────────────────────────
  const handleZoomIn = () => setZoom((z) => Math.min(3, z + 0.15));
  const handleZoomOut = () => setZoom((z) => Math.max(0.3, z - 0.15));
  const handleFitView = () => {
    applyViewBox();
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Recompute the viewBox once the SVG is measured (first render gives no rect)
  // and whenever it resizes, so nodes keep their fixed on-screen pixel size.
  useEffect(() => {
    if (!nodes.length) return;
    applyViewBox();
    const el = svgRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => applyViewBox());
    ro.observe(el);
    return () => ro.disconnect();
  }, [nodes, applyViewBox]);

  // ─── Auto-center on the currently running ("In Progress") node ────────────
  useEffect(() => {
    if (progressView || !nodes.length || !dagNodes.length || !viewBox) return;
    // Find the first running node present in the laid-out nodes.
    const runningNode = nodes.find((n) =>
      dagNodes.some((opId) => `Op_${opId}` === n.id)
    );
    if (!runningNode) return;

    // viewBox = "minX minY width height"
    const parts = String(viewBox).split(/\s+/).map(Number);
    if (parts.length !== 4 || parts.some((v) => Number.isNaN(v))) return;
    const [minX, minY, vbW, vbH] = parts;

    // Center of the running node in diagram coordinates.
    const nodeCx = runningNode.x + (runningNode.w || 150) / 2;
    const nodeCy = runningNode.y + (runningNode.h || 50) / 2;
    // Center of the current viewBox.
    const viewCx = minX + vbW / 2;
    const viewCy = minY + vbH / 2;

    // pan transform is translate(pan.x, pan.y) scale(zoom) in viewBox units,
    // so to move nodeC to viewC: pan = viewC - nodeC*zoom.
    const z = 1.2; // slight zoom-in so the running step is prominent
    setZoom(z);
    setPan({
      x: (viewCx - nodeCx * z),
      y: (viewCy - nodeCy * z),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, dagNodes, viewBox, progressView]);

  // ─── Nodes map for edges ─────────────────────────────────────────────────
  const nodesMap = {};
  nodes.forEach((n) => { nodesMap[n.id] = n; });

  // Compute all edge paths centrally — guarantees no overlaps
  const edgePaths = computeEdgePaths(edges, nodesMap, groups);

  // ─── Drawer ──────────────────────────────────────────────────────────────
  const expandedRowRender = () => {
    if (record?.opNo) return <FinalizedOperationDetailsCollapse record={record} />;
    return <ViewOperationDetailsCollapse record={record} />;
  };

  return (
    <>
      <FullScreen handle={handle}>
        <div
          ref={containerRef}
          style={{
            height: handle.active ? '100vh' : height || '28vh',
            minHeight: handle.active ? '100vh' : height || (progressView ? '32vh' : '28vh'),
            maxHeight: handle.active ? '100vh' : height || '65vh',
            resize: 'vertical',
            overflow: 'hidden',
            position: 'relative',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Legend bar — fixed top row, always visible when diagram is shown */}
          {!progressView && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
                gap: 6,
                padding: '5px 12px 5px 52px',
                borderBottom: '1px solid #f0f0f0',
                flexShrink: 0,
                background: '#fff',
                minHeight: 34,
              }}
            >
              {EXECUTION_STATUS_LEGEND.map((legendMeta) => {
                const cfg = EXECUTION_STATUS_STYLES[legendMeta.key];
                if (!cfg) return null;
                return (
                  <Tooltip key={legendMeta.key} title={legendMeta.description || cfg.label}>
                    <div
                      className="flex items-center gap-1.5 rounded-full border px-2 py-0.5"
                      style={{ borderColor: cfg.color + '55', background: cfg.bg }}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block shrink-0"
                        style={{ backgroundColor: cfg.color }}
                      />
                      <span className="text-xs font-medium whitespace-nowrap" style={{ color: cfg.text }}>
                        {cfg.label}
                      </span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          )}

          {/* Diagram area — flex:1 so it fills remaining height */}
          <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>

            {/* Controls */}
            <div
              style={{
                position: 'absolute',
                left: 8,
                top: 8,
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                background: 'rgba(255,255,255,0.92)',
                borderRadius: 6,
                padding: 4,
                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
              }}
            >
              {data?.batch_number && !progressView && (
                <Tooltip title="View Progress" placement="right">
                  <Button
                    type="text"
                    size="small"
                    icon={<Circle size={16} />}
                    onClick={() => setProgressView(true)}
                  />
                </Tooltip>
              )}
              {!progressView && (
                <>
                  <Tooltip title="Zoom In" placement="right">
                    <Button
                      type="text"
                      size="small"
                      icon={<ZoomIn size={16} />}
                      onClick={handleZoomIn}
                    />
                  </Tooltip>
                  <Tooltip title="Zoom Out" placement="right">
                    <Button
                      type="text"
                      size="small"
                      icon={<ZoomOut size={16} />}
                      onClick={handleZoomOut}
                    />
                  </Tooltip>
                  <Tooltip title="Fit View" placement="right">
                    <Button
                      type="text"
                      size="small"
                      icon={<Minimize size={16} />}
                      onClick={handleFitView}
                    />
                  </Tooltip>
                </>
              )}
              <Tooltip title={handle.active ? 'Exit Full Screen' : 'Full Screen'} placement="right">
                <Button
                  type="text"
                  size="small"
                  icon={handle.active ? <Minimize2 size={16} /> : <Maximize size={16} />}
                  onClick={() => {
                    handle.active ? handle.exit() : handle.enter();
                  }}
                />
              </Tooltip>
              {set_flow_data && (
                <Tooltip title="Close" placement="right">
                  <Button
                    type="text"
                    size="small"
                    icon={<X size={16} />}
                    onClick={() => set_flow_data(null)}
                  />
                </Tooltip>
              )}
            </div>

            {/* SVG Canvas — empty/loading state */}
            {!progressView && nodes.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#aaa',
                  fontSize: 14,
                }}
              >
                {allData ? 'No operations found' : 'Loading workflow...'}
              </div>
            )}
            {!progressView && nodes.length > 0 && (
              <svg
                ref={svgRef}
                width="100%"
                height="100%"
                viewBox={viewBox}
                preserveAspectRatio="xMidYMid meet"
                style={{ cursor: isPanning ? 'grabbing' : 'grab', display: 'block' }}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
              >
                <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
                {/* Layer 1: Group boxes (bottom) */}
                {groups.map((g) => (
                  <ArchiMateGroupBox key={g.id} group={g} />
                ))}
                {/* Layer 2: Edges */}
                {edges.map((e) => (
                  <ArchiMateEdge key={e.id} edgeId={e.id} pathD={edgePaths[e.id]} depType={e.depType} sourceColor={e.sourceColor} machineGroupNames={e.machineGroupNames} />
                ))}
                {/* Layer 3: Nodes */}
                {nodes.map((n) => (
                  <ArchiMateNode
                    key={n.id}
                    node={n}
                    isHighlighted={
                      (operationDagEdit && `Op_${operationDagEdit}` === n.id) ||
                      dagNodes.some((opId) => `Op_${opId}` === n.id)
                    }
                    onClick={handleNodeClick}
                  />
                ))}
                {/* Layer 4: Group labels (always on top) */}
                {groups.map((g) => (
                  <ArchiMateGroupLabel key={`lbl-${g.id}`} group={g} />
                ))}
                {/* Layer 5: Machine-group edge labels — placed at the middle of
                    the edge, drawn last so they sit above nodes. */}
                {edges
                  .filter((e) => e.depType === 'machine' && e.machineGroupNames)
                  .map((e) => {
                    // Prefer the actual path midpoint; fall back to the midpoint of
                    // the two node centers if the path isn't available.
                    const pt = midPointOfPath(edgePaths[e.id]);
                    let x, y;
                    if (pt) {
                      x = pt.x; y = pt.y;
                    } else {
                      const s = nodesMap[e.source];
                      const t = nodesMap[e.target];
                      if (!t) return null;
                      const tx = t.x + (t.w || 120) / 2;
                      const ty = t.y + (t.h || 36) / 2;
                      if (s) {
                        x = (s.x + (s.w || 120) / 2 + tx) / 2;
                        y = (s.y + (s.h || 36) / 2 + ty) / 2;
                      } else {
                        x = tx; y = t.y - 26;
                      }
                    }
                    const fontSize = 10;
                    const boxH = 18;
                    // Compact pill: short preview + "…", full name on hover.
                    const full = e.machineGroupNames;
                    const MAX = 10;
                    const short = full.length > MAX ? `${full.slice(0, MAX).trimEnd()}…` : full;
                    const boxW = short.length * 5.6 + 14;
                    return (
                      <g key={`mglbl-${e.id}`} transform={`translate(${x},${y})`} style={{ cursor: 'default' }}>
                        <title>{full}</title>
                        <rect
                          x={-boxW / 2}
                          y={-boxH / 2}
                          width={boxW}
                          height={boxH}
                          rx={5}
                          fill="#16a34a"
                          stroke="#fff"
                          strokeWidth={1.2}
                        />
                        <text x={0} y={1} textAnchor="middle" dominantBaseline="middle" fontSize={fontSize} fontWeight={700} fill="#fff">
                          {short}
                        </text>
                      </g>
                    );
                  })}
                </g>
              </svg>
            )}

            {/* Progress view */}
            {progressView && (
              <BatchProgressView
                progressView={progressView}
                setProgressView={setProgressView}
                batch_number={data?.batch_number}
              />
            )}
          </div>{/* end diagram area */}
        </div>
      </FullScreen>

      {/* Drawer */}
      <Drawer
        zIndex={2001}
        title="Details"
        placement="right"
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        mask={false}
        size="large"
      >
        {record && (
          <Descriptions
            title="Operation Details"
            size="small"
            bordered
            column={2}
            labelStyle={{ width: '200px' }}
            contentStyle={{ width: '300px' }}
            items={
              record?.opNo
                ? [
                    { label: 'Op No', children: record?.opNo },
                    {
                      label: 'Operation',
                      children: (
                        <Badge
                          color={
                            record?.timeFrom !== 'None' && record?.timeTo === 'None'
                              ? 'orange'
                              : record?.timeFrom !== 'None' && record?.timeTo !== 'None'
                              ? 'green'
                              : 'red'
                          }
                          text={record.operation}
                        />
                      ),
                    },
                    {
                      label: 'Used Machines',
                      children:
                        record?.detailsRemarks?.selected_machine
                          ?.map((m) => m?.machine_name)
                          .join(', ') || '',
                    },
                    { label: 'Start Time', children: formatDateTime(record?.timeFrom) },
                    { label: 'End Time', children: formatDateTime(record?.timeTo) },
                    {
                      label: 'Duration',
                      children: (() => {
                        if (record?.duration === '' && record?.timeFrom !== 'None')
                          return <span style={{ color: '#1e90ff' }}>Running</span>;
                        if (record?.duration === '' && record?.timeFrom === 'None')
                          return <span style={{ color: 'red' }}>Pending</span>;
                        return <span>{record?.duration?.split('.')[0]}</span>;
                      })(),
                    },
                    { label: 'Schedule Time', children: formatDateTime(record?.batch_schedule_time) },
                    {
                      label: 'Selected Machine',
                      children: record?.detailsRemarks?.selected_machine?.[0]?.machine_name || '',
                    },
                    { label: 'Employee Name', children: record?.employee_name?.join(', ') },
                  ]
                : [
                    { label: 'Op No', children: record?.operation_number },
                    { label: 'Operation Name', children: record?.operation_name },
                    { label: 'Operation Type', children: record?.operation_type },
                    { label: 'Duration', children: record?.max_duration },
                    { label: 'Machines', children: record?.machine_type },
                    { label: 'Department', children: record?.department_name },
                  ]
            }
          />
        )}
        <br />
        {record && expandedRowRender()}
      </Drawer>
    </>
  );
}

export default function ArchiMateFlow({ data, set_flow_data, isClickable, height }) {
  console.log('[ArchiMateFlow] RENDER - data:', data);
  return (
    <ArchiMateFlowInner
      data={data}
      set_flow_data={set_flow_data}
      isClickable={isClickable}
      height={height}
    />
  );
}
